import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import Lenis from "lenis";

gsap.registerPlugin(ScrollTrigger);

export function prefersReducedMotion() {
  return typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

var lenisInstance = null;

// Mounts a single Lenis smooth-scroll instance synced to GSAP's ticker.
// No-op (and returns null) when the user prefers reduced motion — native
// scroll behaviour is left untouched in that case.
export function startSmoothScroll() {
  if (typeof window === "undefined" || prefersReducedMotion()) return null;
  if (lenisInstance) return lenisInstance;

  lenisInstance = new Lenis({
    duration: 1.35,
    easing: function(t) { return 1 - Math.pow(1 - t, 4); }, // quartic ease-out — smoother, longer glide than Lenis's default
    smoothWheel: true,
    wheelMultiplier: 1,
  });
  lenisInstance.on("scroll", ScrollTrigger.update);
  gsap.ticker.add(function(time) { lenisInstance.raf(time * 1000); });
  gsap.ticker.lagSmoothing(0);

  return lenisInstance;
}

export function getLenis() {
  return lenisInstance;
}

// Scrolls to a selector/element through Lenis (so anchor jumps use the same
// glide as wheel scrolling) and falls back to native scrollIntoView when
// Lenis isn't running (reduced motion, or not yet mounted).
export function smoothScrollTo(target, options) {
  var opts = options || {};
  if (lenisInstance) {
    lenisInstance.scrollTo(target, { offset: opts.offset || 0, duration: opts.duration || 1.35 });
    return;
  }
  var el = typeof target === "string" ? document.querySelector(target) : target;
  if (el && el.scrollIntoView) el.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth" });
}

export function stopSmoothScroll() {
  if (lenisInstance) {
    gsap.ticker.remove(function(time) { lenisInstance.raf(time * 1000); });
    lenisInstance.destroy();
    lenisInstance = null;
  }
}

// Scroll-triggered fade/stagger reveal for a container's children (or a
// selector within it). Skips animating entirely under reduced-motion —
// content is never hidden by CSS, only by an inline "from" state GSAP sets
// at animation time, so it stays visible if JS never runs.
export function useReveal(ref, options) {
  var opts = options || {};
  useGSAP(function() {
    if (!ref.current || prefersReducedMotion()) return;
    var targets = opts.selector ? ref.current.querySelectorAll(opts.selector) : ref.current.children;
    if (!targets || !targets.length) return;
    gsap.from(targets, {
      opacity: 0,
      y: opts.y != null ? opts.y : 24,
      duration: opts.duration || 0.6,
      ease: opts.ease || "power2.out",
      stagger: opts.stagger != null ? opts.stagger : 0.08,
      scrollTrigger: {
        trigger: ref.current,
        start: opts.start || "top 85%",
        toggleActions: "play none none reverse",
      },
    });
  }, { scope: ref, dependencies: opts.deps || [] });
}

// Mount-triggered fade/stagger reveal (for above-the-fold content like the
// hero, which should animate in immediately rather than wait on scroll).
export function useEnter(ref, options) {
  var opts = options || {};
  useGSAP(function() {
    if (!ref.current || prefersReducedMotion()) return;
    var targets = opts.selector ? ref.current.querySelectorAll(opts.selector) : ref.current.children;
    if (!targets || !targets.length) return;
    gsap.from(targets, {
      opacity: 0,
      y: opts.y != null ? opts.y : 28,
      duration: opts.duration || 0.7,
      ease: opts.ease || "power3.out",
      stagger: opts.stagger != null ? opts.stagger : 0.1,
      delay: opts.delay || 0,
    });
  }, { scope: ref, dependencies: opts.deps || [] });
}

// Pointer-driven 3D tilt for cards. Transform-only (rotateX/rotateY/scale
// via transformPerspective on the same element), capped rotation, and
// fully inert under reduced-motion. Attach the returned ref to the card.
export function useTilt(options) {
  var opts = options || {};
  var maxTilt = opts.maxTilt != null ? opts.maxTilt : 8;
  var ref = useRef(null);

  useEffect(function() {
    var el = ref.current;
    if (!el || prefersReducedMotion()) return;

    gsap.set(el, { transformPerspective: 800, transformStyle: "preserve-3d" });
    var rotX = gsap.quickTo(el, "rotateX", { duration: 0.5, ease: "power3.out" });
    var rotY = gsap.quickTo(el, "rotateY", { duration: 0.5, ease: "power3.out" });
    var scaleTo = gsap.quickTo(el, "scale", { duration: 0.4, ease: "power3.out" });

    function pointFromEvent(e) {
      return e.touches && e.touches[0] ? e.touches[0] : e;
    }
    function handleMove(e) {
      var rect = el.getBoundingClientRect();
      var p = pointFromEvent(e);
      var px = (p.clientX - rect.left) / rect.width;
      var py = (p.clientY - rect.top) / rect.height;
      rotY((px - 0.5) * maxTilt * 2);
      rotX(-(py - 0.5) * maxTilt * 2);
    }
    function handleEnter() { scaleTo(1.02); }
    function handleLeave() { rotX(0); rotY(0); scaleTo(1); }

    el.addEventListener("mousemove", handleMove);
    el.addEventListener("mouseenter", handleEnter);
    el.addEventListener("mouseleave", handleLeave);
    el.addEventListener("touchmove", handleMove, { passive: true });
    el.addEventListener("touchend", handleLeave);

    return function() {
      el.removeEventListener("mousemove", handleMove);
      el.removeEventListener("mouseenter", handleEnter);
      el.removeEventListener("mouseleave", handleLeave);
      el.removeEventListener("touchmove", handleMove);
      el.removeEventListener("touchend", handleLeave);
    };
  }, [maxTilt]);

  return ref;
}

// Splits a display stat like "40K+" / "98%" / "3×" into a numeric part to
// count up and a trailing suffix to keep static.
export function parseStatValue(raw) {
  var match = String(raw).match(/^([\d.]+)(.*)$/);
  if (!match) return { value: null, decimals: 0, suffix: raw };
  return { value: parseFloat(match[1]), decimals: (match[1].split(".")[1] || "").length, suffix: match[2] };
}

// Counts a stat number up from 0 on scroll-into-view; falls back to the
// final label immediately when reduced-motion is on or parsing fails.
export function useCountUp(ref, rawValue, options) {
  var opts = options || {};
  useGSAP(function() {
    if (!ref.current) return;
    var parsed = parseStatValue(rawValue);
    if (parsed.value == null || prefersReducedMotion()) {
      ref.current.textContent = rawValue;
      return;
    }
    var counter = { val: 0 };
    gsap.to(counter, {
      val: parsed.value,
      duration: opts.duration || 1.4,
      ease: "power2.out",
      scrollTrigger: { trigger: ref.current, start: "top 90%", toggleActions: "play none none none" },
      onUpdate: function() {
        ref.current.textContent = counter.val.toFixed(parsed.decimals) + parsed.suffix;
      },
      onComplete: function() {
        ref.current.textContent = rawValue;
      },
    });
  }, { scope: ref, dependencies: [rawValue] });
}
